import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

async function fetchMe() {
  try {
    const { data } = await api.get("/auth/me");
    return data;
  } catch (err) {
    if (err.response?.status !== 401) {
      console.error("auth/me failed", err);
    }
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const didInit = useRef(false);

  const initAuth = useCallback(async () => {
    // If returning from OAuth callback, AuthCallback will run the session
    // exchange. Skip the /me check here so we don't race.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const data = await fetchMe();
    setUser(data);
    setLoading(false);
  }, []);

  const refresh = useCallback(async () => {
    const data = await fetchMe();
    setUser(data);
    return data;
  }, []);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    initAuth();
  }, [initAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (email, password, name, ref) => {
    const { data } = await api.post("/auth/signup", { email, password, name, ref });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("logout failed", err);
    }
    setUser(null);
  }, []);

  // Memoise the context value so child components only re-render when the
  // user object or loading flag actually change — not every parent render.
  const value = useMemo(
    () => ({ user, loading, login, signup, logout, refresh, setUser }),
    [user, loading, login, signup, logout, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
